import { cn } from '@/lib/cn';
import type { InterruptPrompt, InterruptResolveRequest } from '@innermate/shared';
import { CornerDownLeft, Pencil, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Markdown } from './Markdown';

interface Props {
  interruptId: string;
  prompt: InterruptPrompt;
  onResolve: (body: InterruptResolveRequest) => void;
  /** 사용자가 X 또는 Esc 로 닫음 — onResolve 와 별개로 호출되어 skip 으로 처리. */
  onClose: () => void;
  pending?: boolean;
  /**
   * 'fixed' (기본) — viewport 하단 sticky 카드 (max-w-3xl, 일반 chat 풀스크린).
   * 'embedded' — 부모 컨테이너 안에 inline 카드. 슬라이드 편집기 좌측 360px 패널처럼
   *   좁은 컨테이너에서 자연스럽게 채팅 흐름과 이어진다.
   */
  variant?: 'fixed' | 'embedded';
}

/**
 * Human-in-the-loop 응답 모달.
 *
 * - 1–9 키: 옵션 즉시 선택 (free-text 모드 아닐 때)
 * - ↑/↓ + Enter: 포커스 이동 후 선택
 * - e/E 또는 "기타" 클릭: 자유 입력 모드 진입
 * - Esc: 자유 입력 모드면 모드만 해제. 아니면 모달 닫기 (skip).
 *
 * 디자인은 design/human-in-the-loop-example2.png — 하단 sticky 카드.
 */
export function HilModal({
  interruptId: _interruptId,
  prompt,
  onResolve,
  onClose,
  pending,
  variant = 'fixed',
}: Props) {
  const [activeIdx, setActiveIdx] = useState(
    Math.min(prompt.defaultIndex, Math.max(0, prompt.choices.length - 1)),
  );
  // choices 가 비어 있고 자유 입력만 가능하면 mount 시 곧장 freeMode 로.
  // 모델이 ask_user(choices=[], allowFreeText=true) 로 호출했을 때
  // 사용자에게 textarea 가 즉시 보여야 stuck 으로 인식되지 않음.
  const [freeMode, setFreeMode] = useState(prompt.choices.length === 0 && prompt.allowFreeText);
  const [freeText, setFreeText] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const choiceCount = prompt.choices.length;
  const choicelessFree = choiceCount === 0 && prompt.allowFreeText;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (pending) return;
      // IME 입력 중엔 단축키 무시 (한글 조합)
      if (e.isComposing || e.keyCode === 229) return;

      if (freeMode) {
        if (e.key === 'Escape') {
          e.preventDefault();
          setFreeMode(false);
          setFreeText('');
        }
        // Enter 와 일반 텍스트는 input 자체 onKeyDown 이 처리.
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        // allowSkip=false 면 Esc 로 닫을 수 없다 (반드시 응답 필요한 인터럽트).
        if (prompt.allowSkip) onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (choiceCount === 0 ? 0 : Math.min(choiceCount - 1, i + 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const c = prompt.choices[activeIdx];
        if (c) onResolve({ kind: 'choice', choiceId: c.id });
      } else if (/^[1-9]$/.test(e.key)) {
        const i = Number(e.key) - 1;
        const c = prompt.choices[i];
        if (c) {
          e.preventDefault();
          onResolve({ kind: 'choice', choiceId: c.id });
        }
      } else if (prompt.allowFreeText && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault();
        setFreeMode(true);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIdx, choiceCount, freeMode, onClose, onResolve, prompt, pending]);

  const submitFree = () => {
    const text = freeText.trim();
    if (!text) return;
    onResolve({ kind: 'free', text });
  };

  return (
    <div
      className={cn(
        variant === 'fixed'
          ? 'absolute inset-x-0 bottom-0 z-40 px-4 pb-4 pointer-events-none'
          : 'w-full',
      )}
      aria-live="polite"
    >
      <div
        className={cn(
          'rounded-2xl border border-border bg-surface shadow-[var(--shadow-card)] overflow-hidden',
          variant === 'fixed' ? 'mx-auto max-w-3xl pointer-events-auto' : 'w-full',
        )}
      >
        <div className="flex items-center justify-between px-4 h-10 border-b border-border">
          <span className="text-sm text-foreground-muted">{prompt.title}</span>
          {prompt.allowSkip ? (
            <button
              type="button"
              aria-label="닫기"
              onClick={onClose}
              disabled={pending}
              className="size-7 grid place-items-center text-foreground-muted hover:text-foreground disabled:opacity-50"
            >
              <X className="size-4" />
            </button>
          ) : (
            <span className="size-7" aria-hidden />
          )}
        </div>

        {prompt.prompt ? (
          <div className="px-4 py-3 border-b border-border overflow-auto max-h-72">
            <Markdown
              content={prompt.prompt}
              className="text-xs text-foreground-muted [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-surface-overlay [&_th]:font-medium [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_p]:my-1 [&_strong]:text-foreground"
            />
          </div>
        ) : null}

        {choicelessFree ? (
          <div className="px-4 py-3 border-b border-border">
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  submitFree();
                }
              }}
              placeholder={`${prompt.title} — 자유롭게 답해주세요. (⌘/Ctrl + Enter 전송)`}
              rows={3}
              disabled={pending}
              className="w-full resize-none bg-surface-muted/30 rounded-md border border-border px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
              aria-label="자유 응답 입력"
            />
            <div className="flex items-center justify-end gap-2 mt-2">
              {prompt.allowSkip ? (
                <button
                  type="button"
                  onClick={onClose}
                  disabled={pending}
                  className="px-3 h-8 rounded-md border border-border text-xs text-foreground-muted hover:text-foreground disabled:opacity-50"
                >
                  건너뛰기
                </button>
              ) : null}
              <button
                type="button"
                onClick={submitFree}
                disabled={pending || !freeText.trim()}
                className="px-4 h-8 rounded-md bg-accent text-accent-foreground text-xs font-medium hover:bg-accent/90 disabled:opacity-50"
              >
                응답 보내기
              </button>
            </div>
          </div>
        ) : null}

        {/*
          Picker-card 모드 — 비주얼 디렉션 선택처럼 카드 그리드 + 큰 swatch 미리보기.
          choices 의 swatch 가 4개 이상 (= 색 세트 5색 같은 케이스) 이면 자동 전환.
          그 외엔 기존 list (라벨 옆 작은 사각형) 그대로.
        */}
        {!choicelessFree &&
        prompt.choices.length > 0 &&
        prompt.choices.every((c) => c.swatch && c.swatch.length >= 4) ? (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 max-h-[60vh] overflow-y-auto"
            aria-label={prompt.title}
          >
            {prompt.choices.map((c, i) => {
              const active = !freeMode && i === activeIdx;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => !pending && onResolve({ kind: 'choice', choiceId: c.id })}
                  onMouseMove={() => setActiveIdx(i)}
                  disabled={pending}
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors',
                    active
                      ? 'border-accent ring-2 ring-accent/30 bg-surface'
                      : 'border-border hover:bg-surface-muted/60',
                    pending ? 'opacity-50 cursor-not-allowed' : '',
                  )}
                >
                  <div className="text-sm font-medium text-foreground leading-tight">{c.label}</div>
                  {c.hint ? (
                    <div className="text-xs text-foreground-muted leading-snug line-clamp-2">
                      {c.hint}
                    </div>
                  ) : null}
                  <div className="flex gap-1 mt-auto pt-1" aria-hidden>
                    {(c.swatch ?? []).slice(0, 5).map((color, sIdx) => (
                      <span
                        key={sIdx}
                        className="flex-1 h-7 rounded-sm border border-border/40"
                        style={{ background: color }}
                      />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}

        <ul
          aria-label={prompt.title}
          hidden={
            choicelessFree ||
            (prompt.choices.length > 0 &&
              prompt.choices.every((c) => c.swatch && c.swatch.length >= 4))
          }
        >
          {prompt.choices.map((c, i) => {
            const active = !freeMode && i === activeIdx;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  // biome-ignore lint/a11y/useSemanticElements: <option> 은 <select> 안에서만 유효 — 커스텀 listbox 패턴
                  role="option"
                  aria-selected={active}
                  onClick={() => !pending && onResolve({ kind: 'choice', choiceId: c.id })}
                  onMouseMove={() => setActiveIdx(i)}
                  disabled={pending}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 h-12 text-sm text-left transition-colors',
                    active ? 'bg-surface-muted' : 'hover:bg-surface-muted/60',
                    pending ? 'opacity-50 cursor-not-allowed' : '',
                  )}
                >
                  <span
                    className={cn(
                      'size-6 grid place-items-center rounded-md text-xs font-mono shrink-0',
                      active
                        ? 'bg-surface text-foreground border border-border'
                        : 'text-foreground-muted',
                    )}
                  >
                    {i + 1}
                  </span>
                  {c.swatch && c.swatch.length > 0 ? (
                    <span className="flex shrink-0 gap-0.5" aria-hidden>
                      {c.swatch.slice(0, 8).map((color, sIdx) => (
                        <span
                          key={sIdx}
                          className="size-7 rounded border border-foreground/15 shadow-sm"
                          style={{ background: color }}
                        />
                      ))}
                    </span>
                  ) : null}
                  <span className="flex-1 truncate">
                    <span className="text-foreground">{c.label}</span>
                    {c.hint ? (
                      <span className="ml-2 text-xs text-foreground-muted">{c.hint}</span>
                    ) : null}
                  </span>
                  {active ? (
                    <CornerDownLeft className="size-4 text-foreground-muted shrink-0" />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>

        {!choicelessFree && (prompt.allowFreeText || prompt.allowSkip) ? (
          <div className="flex items-center gap-2 px-4 h-12 border-t border-border">
            {prompt.allowFreeText ? (
              freeMode ? (
                <input
                  ref={inputRef as React.RefObject<HTMLInputElement>}
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.nativeEvent.isComposing) return;
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submitFree();
                    }
                  }}
                  placeholder={prompt.freeTextPlaceholder ?? '직접 입력하고 Enter…'}
                  disabled={pending}
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-foreground-muted disabled:opacity-50"
                  aria-label="직접 입력"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setFreeMode(true);
                    window.setTimeout(() => inputRef.current?.focus(), 0);
                  }}
                  disabled={pending}
                  className="flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground disabled:opacity-50"
                >
                  <Pencil className="size-4" />
                  <span>{prompt.freeTextLabel ?? '기타'}</span>
                </button>
              )
            ) : null}

            <div className="flex-1" />

            {prompt.allowSkip ? (
              <button
                type="button"
                onClick={onClose}
                disabled={pending}
                className="px-3 h-7 rounded-md border border-border text-xs text-foreground-muted hover:text-foreground disabled:opacity-50"
              >
                건너뛰기
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
